import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { 
  SeasonalEventConfig, 
  SeasonalEventType, 
  defaultEventConfig,
  isNewYearPeriod 
} from '@/config/seasonalEvents';
import { useTranslation } from 'react-i18next';

interface SeasonalEventContextType {
  // Current active event
  activeEvent: SeasonalEventType;
  eventConfig: SeasonalEventConfig;
  isEventActive: boolean;
  
  // Feature flags
  showSeasonalBadges: boolean;
  showSeasonalChallenge: boolean;
  showConfettiEffects: boolean;
  showFireworkEffects: boolean;
  
  // Theme utilities
  getSeasonalAccentClass: () => string;
  getBannerMessage: () => string | null;
  
  // Confetti trigger (with rate limiting)
  triggerConfetti: () => void;
  canTriggerConfetti: boolean;
  
  // Loading state
  isLoading: boolean;
  
  // Refetch config
  refetchConfig: () => Promise<void>;
}

const SeasonalEventContext = createContext<SeasonalEventContextType | null>(null);

// Rate limit confetti to once per 30 seconds
const CONFETTI_COOLDOWN_MS = 30000;

export const SeasonalEventProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const [eventConfig, setEventConfig] = useState<SeasonalEventConfig>(defaultEventConfig);
  const [isLoading, setIsLoading] = useState(true);
  const [lastConfettiTime, setLastConfettiTime] = useState(0);
  const confettiTriggerRef = useRef<(() => void) | null>(null);

  const fetchEventConfig = useCallback(async () => {
    try {
      const now = new Date().toISOString();
      
      // Fetch active seasonal event from database
      const { data, error } = await supabase
        .from('seasonal_events')
        .select('*')
        .eq('is_enabled', true)
        .lte('start_date', now)
        .gte('end_date', now)
        .order('start_date', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('[SeasonalEvent] Error fetching config:', error);
        // Fall back to client-side detection
        if (isNewYearPeriod()) {
          setEventConfig({
            ...defaultEventConfig,
            eventType: 'NEW_YEAR',
            isEnabled: true,
            displayName: 'New Year Sale',
          });
        }
        return;
      }

      if (data) {
        setEventConfig({
          eventType: data.event_type as SeasonalEventType,
          displayName: data.display_name,
          isEnabled: data.is_enabled,
          startDate: new Date(data.start_date),
          endDate: new Date(data.end_date),
          promoCode: data.promo_code,
          bannerMessage: data.banner_message,
          bannerMessageDa: data.banner_message_da,
          discountPercent: data.discount_percent,
          themeConfig: (data.theme_config as SeasonalEventConfig['themeConfig']) || {},
        });
      } else {
        // No active event
        setEventConfig(defaultEventConfig);
      }
    } catch (err) {
      console.error('[SeasonalEvent] Fetch error:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEventConfig();
    
    // Refresh config every 5 minutes
    const interval = setInterval(fetchEventConfig, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchEventConfig]);

  const activeEvent = eventConfig.isEnabled ? eventConfig.eventType : null;
  const isEventActive = activeEvent !== null;

  // Feature flags based on active event
  const showSeasonalBadges = isEventActive && activeEvent === 'NEW_YEAR';
  const showSeasonalChallenge = isEventActive && activeEvent === 'NEW_YEAR';
  const showConfettiEffects = isEventActive && (eventConfig.themeConfig.showConfetti ?? false);
  const showFireworkEffects = isEventActive && (eventConfig.themeConfig.showFireworks ?? false);

  // Get accent class based on event
  const getSeasonalAccentClass = useCallback(() => {
    switch (activeEvent) {
      case 'NEW_YEAR':
        return 'seasonal-new-year';
      case 'CHRISTMAS':
        return 'seasonal-christmas';
      case 'VALENTINE':
        return 'seasonal-valentine';
      case 'HALLOWEEN':
        return 'seasonal-halloween';
      default:
        return '';
    }
  }, [activeEvent]);

  // Get banner message in current language
  const getBannerMessage = useCallback(() => {
    if (!isEventActive) return null;
    const isDanish = i18n.language === 'da';
    return isDanish ? eventConfig.bannerMessageDa : eventConfig.bannerMessage;
  }, [isEventActive, eventConfig, i18n.language]);

  // Rate-limited confetti trigger
  const canTriggerConfetti = Date.now() - lastConfettiTime > CONFETTI_COOLDOWN_MS;
  
  const triggerConfetti = useCallback(() => {
    if (!showConfettiEffects || !canTriggerConfetti) return;
    setLastConfettiTime(Date.now());
    
    // Dispatch custom event for confetti components to listen to
    window.dispatchEvent(new CustomEvent('seasonal-confetti'));
  }, [showConfettiEffects, canTriggerConfetti]);

  // Register confetti trigger globally
  useEffect(() => {
    confettiTriggerRef.current = triggerConfetti;
  }, [triggerConfetti]);

  return (
    <SeasonalEventContext.Provider
      value={{
        activeEvent,
        eventConfig,
        isEventActive,
        showSeasonalBadges,
        showSeasonalChallenge,
        showConfettiEffects,
        showFireworkEffects,
        getSeasonalAccentClass,
        getBannerMessage,
        triggerConfetti,
        canTriggerConfetti,
        isLoading,
        refetchConfig: fetchEventConfig,
      }}
    >
      {children}
    </SeasonalEventContext.Provider>
  );
};

export const useSeasonalEvent = () => {
  const context = useContext(SeasonalEventContext);
  if (!context) {
    throw new Error('useSeasonalEvent must be used within SeasonalEventProvider');
  }
  return context;
};

// Hook for triggering confetti on specific actions
export const useSeasonalConfetti = () => {
  const { triggerConfetti, showConfettiEffects, canTriggerConfetti } = useSeasonalEvent();
  
  return {
    trigger: triggerConfetti,
    isEnabled: showConfettiEffects,
    canTrigger: canTriggerConfetti,
  };
};
